"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { about } from "@data";
import Image from "next/image";
import { Card, Chip } from "@heroui/react";
import { ExternalLink } from "lucide-react";
import { type MouseEvent, type TouchEvent, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useInView } from "framer-motion";
const WORD_STAGGER = 0.08;
const WORD_DURATION = 0.4;
const DURATION = 2.0;
const REPEAT_DELAY = 5.0;
const pseudoRandom = (seed: number) => {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
};
const ThumbnailWithReveal = t0 => {
  const $ = _c(32);
  const {
    src,
    alt
  } = t0;
  const ref = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [-0.5, 0.5];
    t2 = [12, -12];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      stiffness: 300,
      damping: 30
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const rotateX = useSpring(useTransform(mouseY, t1, t2), t3);
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [-0.5, 0.5];
    t5 = [-12, 12];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      stiffness: 300,
      damping: 30
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const rotateY = useSpring(useTransform(mouseX, t4, t5), t6);
  let t7;
  if ($[6] !== mouseX || $[7] !== mouseY) {
    t7 = e => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
      mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
    };
    $[6] = mouseX;
    $[7] = mouseY;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleMouseMove = t7;
  let t8;
  if ($[9] !== mouseX || $[10] !== mouseY) {
    t8 = () => {
      mouseX.set(0);
      mouseY.set(0);
    };
    $[9] = mouseX;
    $[10] = mouseY;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleMouseLeave = t8;
  let t9;
  if ($[12] !== mouseX || $[13] !== mouseY) {
    t9 = e_0 => {
      const touch = e_0.touches[0];
      const rect_0 = ref.current?.getBoundingClientRect();
      if (!rect_0 || !touch) {
        return;
      }
      mouseX.set((touch.clientX - rect_0.left) / rect_0.width - 0.5);
      mouseY.set((touch.clientY - rect_0.top) / rect_0.height - 0.5);
    };
    $[12] = mouseX;
    $[13] = mouseY;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const handleTouchMove = t9;
  let t10;
  if ($[15] !== mouseX || $[16] !== mouseY) {
    t10 = () => {
      mouseX.set(0);
      mouseY.set(0);
    };
    $[15] = mouseX;
    $[16] = mouseY;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleTouchEnd = t10;
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      perspective: 800
    };
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== rotateX || $[20] !== rotateY) {
    t12 = {
      rotateX,
      rotateY,
      transformStyle: "preserve-3d"
    };
    $[19] = rotateX;
    $[20] = rotateY;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== alt || $[23] !== src) {
    t13 = <Image alt={alt} className="rounded-3xl" fill={true} sizes="100vw, (min-width: 768px) 640px" src={src} />;
    $[22] = alt;
    $[23] = src;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== handleMouseLeave || $[26] !== handleMouseMove || $[27] !== handleTouchEnd || $[28] !== handleTouchMove || $[29] !== t12 || $[30] !== t13) {
    t14 = <div style={t11} className="size-full"><motion.div ref={ref} className="border-border relative size-full rounded-3xl border" style={t12} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>{t13}</motion.div></div>;
    $[25] = handleMouseLeave;
    $[26] = handleMouseMove;
    $[27] = handleTouchEnd;
    $[28] = handleTouchMove;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
};
const ProjectContent = t0 => {
  const $ = _c(58);
  const {
    name,
    description,
    url,
    githubUrl
  } = t0;
  const ref = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      once: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const inView = useInView(ref, t1);
  let T0;
  let T1;
  let T2;
  let descriptionDelay;
  let t10;
  let t11;
  let t12;
  let t13;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== githubUrl || $[2] !== inView || $[3] !== name || $[4] !== url) {
    const words = name.split(" ");
    descriptionDelay = (words.length - 1) * WORD_STAGGER + WORD_DURATION + 0.1;
    t12 = ref;
    t13 = "flex w-full flex-col justify-center gap-2";
    T2 = motion.div;
    t10 = "hover";
    t11 = "flex w-fit items-center gap-1";
    T1 = Link;
    t6 = "flex items-center gap-1 hover:gap-2";
    t7 = url || githubUrl;
    t8 = "_blank";
    t9 = "noreferrer noopener";
    T0 = motion.h3;
    t2 = "font-semibold";
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        hover: {
          scale: 1.05
        }
      };
      t4 = {
        duration: 0.2,
        ease: "easeOut"
      };
      $[21] = t3;
      $[22] = t4;
    } else {
      t3 = $[21];
      t4 = $[22];
    }
    let t14;
    if ($[23] !== inView) {
      t14 = (word, i) => <motion.span key={i} className="mr-1 inline-block" initial={{
        opacity: 0,
        x: -20
      }} animate={inView ? {
        opacity: 1,
        x: 0
      } : {}} transition={{
        duration: WORD_DURATION,
        delay: i * WORD_STAGGER,
        ease: "easeOut"
      }}>{word}</motion.span>;
      $[23] = inView;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    t5 = words.map(t14);
    $[1] = githubUrl;
    $[2] = inView;
    $[3] = name;
    $[4] = url;
    $[5] = T0;
    $[6] = T1;
    $[7] = T2;
    $[8] = descriptionDelay;
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    T0 = $[5];
    T1 = $[6];
    T2 = $[7];
    descriptionDelay = $[8];
    t10 = $[9];
    t11 = $[10];
    t12 = $[11];
    t13 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t14;
  if ($[25] !== T0 || $[26] !== t2 || $[27] !== t3 || $[28] !== t4 || $[29] !== t5) {
    t14 = <T0 className={t2} variants={t3} transition={t4}>{t5}</T0>;
    $[25] = T0;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <motion.div variants={{
      hover: {
        x: [0, -3, 3, -3, 3, 0]
      }
    }} transition={{
      duration: 0.4,
      ease: "easeInOut"
    }}><ExternalLink size={16} /></motion.div>;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== T1 || $[33] !== t14 || $[34] !== t6 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t16 = <T1 className={t6} href={t7} target={t8} rel={t9}>{t14}{t15}</T1>;
    $[32] = T1;
    $[33] = t14;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== T2 || $[40] !== t10 || $[41] !== t11 || $[42] !== t16) {
    t17 = <T2 whileHover={t10} className={t11}>{t16}</T2>;
    $[39] = T2;
    $[40] = t10;
    $[41] = t11;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      opacity: 0,
      x: -20
    };
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== inView) {
    t19 = inView ? {
      opacity: 1,
      x: 0
    } : {};
    $[45] = inView;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== descriptionDelay) {
    t20 = {
      duration: WORD_DURATION,
      delay: descriptionDelay,
      ease: "easeOut"
    };
    $[47] = descriptionDelay;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== description || $[50] !== t19 || $[51] !== t20) {
    t21 = <motion.p className="text-muted-foreground text-sm" initial={t18} animate={t19} transition={t20}>{description}</motion.p>;
    $[49] = description;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t12 || $[54] !== t13 || $[55] !== t17 || $[56] !== t21) {
    t22 = <div ref={t12} className={t13}>{t17}{t21}</div>;
    $[53] = t12;
    $[54] = t13;
    $[55] = t17;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  return t22;
};
const Projects = () => {
  const $ = _c(8);
  const ref = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      once: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const inView = useInView(ref, t0);
  let t1;
  if ($[1] !== inView) {
    t1 = "Notable Projects".split(" ").map((word, i) => <motion.span key={i} className="mr-1 inline-block" initial={{
      opacity: 0,
      x: -20
    }} animate={inView ? {
      opacity: 1,
      x: 0
    } : {}} transition={{
      duration: WORD_DURATION,
      delay: i * WORD_STAGGER,
      ease: "easeOut"
    }}>{word}</motion.span>);
    $[1] = inView;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <h2 className="text-2xl font-bold">{t1}</h2>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex w-full flex-col items-center justify-center gap-10">{about.projects.map(_temp2)}</div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2) {
    t4 = <div ref={ref} className="flex w-full flex-col justify-center gap-3" id="projects">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export default Projects;
function _temp(skill, index) {
  const Icon = skill.icon;
  const initialDelay = pseudoRandom(index) * 8;
  return <Chip className="gap-1 whitespace-nowrap" color="default" key={skill?.name} size="lg" variant="soft"><motion.div animate={{
      rotate: [0, 360],
      y: [0, -10, 0]
    }} transition={{
      duration: DURATION,
      repeat: Infinity,
      repeatType: "reverse",
      repeatDelay: REPEAT_DELAY,
      delay: initialDelay,
      ease: "easeInOut"
    }}><Icon className="size-4" /></motion.div>{skill.name}</Chip>;
}
function _temp2(project) {
  return <Card className="overflow-visible p-0" key={project.name} variant="transparent"><Card.Header className="relative aspect-2/1 w-full"><ThumbnailWithReveal src={project.thumbnail} alt={project.name} /></Card.Header><Card.Content><ProjectContent name={project.name} description={project.description} url={project.url} githubUrl={project.githubUrl} /></Card.Content><Card.Footer className="flex flex-wrap gap-2">{project.skills.map(_temp)}</Card.Footer></Card>;
}